import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components';
import { SideBarModal } from '@/components/Modal';
import {
  Maintenance,
  MaintenanceCategory,
  MaintenanceStatus,
  Handover,
} from '@/types/common';
import {
  closeMaintenanceCase,
  getMaintenanceById,
  markMaintenanceAsCompleteV2,
  makeMaintenanceDecision,
  requestTempSwap,
  requestPermSwap,
  RequestPermSwapRequest,
  startMaintenanceWork,
  getMaintenanceStatusHistory,
  MaintenanceStatusHistoryEntry,
} from '@/services/api/maintenances';
import { Vehicle } from '@/services/api/vehicles';
import {
  getListHandover,
  ReturnHandoverRequest,
} from '@/services/api/handovers';
import { extractApiErrorMessage, getUserEmail } from '@/utils';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import MaintenanceDetailHeader from './components/MaintenanceDetailHeader';
import MaintenanceDetailContent from './components/MaintenanceDetailContent';
import MaintenanceStatusTimeline from './components/MaintenanceStatusTimeline';
import MaintenanceMarkCompleteModal from './components/MaintenanceMarkCompleteModal';
import MakeDecisionModal from './components/MakeDecisionModal';
import RequestTempSwapModal from './components/RequestTempSwapModal';
import RequestPermSwapModal from './components/RequestPermSwapModal';
import SelectReplacementVehicleModal from './components/SelectReplacementVehicleModal';
import StatusNotification from './components/StatusNotification';
import AddVehicleModal from '@/pages/vehicles/components/OwnedVehicle/AddVehicleModal';
import MaintenanceReturnVehicleModal from './components/MaintenanceReturnVehicleModal';
import ReturnHandoverModal from '@/pages/handovers/components/ReturnHandoverModal';

const getActionButtons = (
  status?: string,
  handlers?: {
    onMarkComplete?: () => void;
    onCloseCase?: () => void;
    onStartWork?: () => void;
    onMakeDecision?: () => void;
    onToggleSwapMenu?: () => void;
    onRequestTempSwap?: () => void;
    onRequestPermSwap?: () => void;
    isSwapMenuOpen?: boolean;
    isMarkingComplete?: boolean;
    isClosingCase?: boolean;
    isStartingWork?: boolean;
    isSubmittingDecision?: boolean;
  }
) => {
  switch (status) {
    case 'OPEN':
      return (
        <Button
          variant="primary"
          size="small"
          onClick={handlers?.onStartWork}
          disabled={handlers?.isStartingWork}
        >
          Start Work
        </Button>
      );
    case 'IN_PROGRESS':
      return (
        <div className="flex gap-2">
          <div className="relative">
            <Button
              variant="secondary"
              size="small"
              onClick={handlers?.onToggleSwapMenu}
            >
              <i className="ri-add-line text-[12px] mr-1" />
              Request Swap
            </Button>
            {handlers?.isSwapMenuOpen && (
              <div className="absolute left-0 top-full z-10 mt-2 w-[200px] rounded-lg border border-gray-200 bg-white py-2 shadow-lg">
                <button
                  type="button"
                  onClick={handlers?.onRequestTempSwap}
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                >
                  <i className="ri-time-line text-[14px]" />
                  Req Temporary Swap
                </button>
                <button
                  type="button"
                  onClick={handlers?.onRequestPermSwap}
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                >
                  <i className="ri-infinity-line text-[14px]" />
                  Req Permanent Swap
                </button>
              </div>
            )}
          </div>
          <Button
            variant="primary"
            size="small"
            onClick={handlers?.onMarkComplete}
            disabled={handlers?.isMarkingComplete}
          >
            <i className="ri-check-line text-[12px] mr-1" />
            Mark as Complete
          </Button>
        </div>
      );
    case 'PENDING_APPROVAL_COMPLETION':
    case 'PENDING_APPROVAL_TEMP_SWAP':
    case 'PENDING_APPROVAL_PERM_SWAP':
      return (
        <div className="flex gap-2">
          <Button
            variant="primary"
            size="small"
            onClick={handlers?.onMakeDecision}
            disabled={handlers?.isSubmittingDecision}
          >
            Make Decision
          </Button>
        </div>
      );
    case 'TEMP_SWAP':
      return (
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="small"
            onClick={handlers?.onRequestPermSwap}
          >
            <i className="ri-add-line text-[12px] mr-1" />
            Request Permanent Swap
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handlers?.onMarkComplete}
            disabled={handlers?.isSubmittingDecision}
          >
            Mark as Complete
          </Button>
        </div>
      );
    case 'PERM_SWAP_WITH_COST':
      return (
        <Button
          variant="primary"
          size="small"
          onClick={handlers?.onMarkComplete}
          disabled={handlers?.isMarkingComplete}
        >
          Mark as Complete
        </Button>
      );
    case 'COMPLETED':
      return (
        <Button
          variant="primary"
          size="small"
          onClick={handlers?.onCloseCase}
          disabled={handlers?.isClosingCase}
        >
          Close Case
        </Button>
      );
    case 'PERM_SWAP':
      return (
        <div className="flex gap-2">
          <Button
            variant="primary"
            size="small"
            onClick={handlers?.onMarkComplete}
            disabled={handlers?.isSubmittingDecision}
          >
            Mark as Complete
          </Button>
        </div>
      );
    default:
      return null;
  }
};

const MaintenanceDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { maintenance?: Maintenance } | null;
  const [maintenance, setMaintenance] = useState<Maintenance | null>(
    state?.maintenance || null
  );
  const [statusHistory, setStatusHistory] = useState<
    MaintenanceStatusHistoryEntry[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMarkCompleteOpen, setIsMarkCompleteOpen] = useState(false);
  const [isSubmittingComplete, setIsSubmittingComplete] = useState(false);
  const [isDecisionOpen, setIsDecisionOpen] = useState(false);
  const [isSubmittingDecision, setIsSubmittingDecision] = useState(false);
  const [isTempSwapOpen, setIsTempSwapOpen] = useState(false);
  const [isSubmittingTempSwap, setIsSubmittingTempSwap] = useState(false);
  const [isPermSwapOpen, setIsPermSwapOpen] = useState(false);
  const [isSubmittingPermSwap, setIsSubmittingPermSwap] = useState(false);
  const [isSelectReplacementOpen, setIsSelectReplacementOpen] = useState(false);
  const [isCreateVehicleOpen, setIsCreateVehicleOpen] = useState(false);
  const [isReturnVehicleOpen, setIsReturnVehicleOpen] = useState(false);
  const [permSwapNewVehicleId, setPermSwapNewVehicleId] = useState<
    string | null
  >(null);
  const [permSwapNewVehicle, setPermSwapNewVehicle] = useState<{
    platNomor: string;
    vin: string;
    imei?: string;
    modelID: string;
    color: string;
    contractDuration?: number;
    contractStartDate: string;
    arriveAt: string;
    stnkLink: string;
    vehicleImageURL: string;
    unitEquipment: string[];
    appUsername: string;
    appPassword: string;
    businessUnit?: string;
    hubID?: string;
    hubName?: string;
    hubCityID?: string;
    hubCityName?: string;
  } | null>(null);
  const [isClosingCase, setIsClosingCase] = useState(false);
  const [isStartingWork, setIsStartingWork] = useState(false);
  const [isSwapMenuOpen, setIsSwapMenuOpen] = useState(false);
  const [isStartWorkOpen, setIsStartWorkOpen] = useState(false);

  // Return handover state (for MINOR_DAMAGE return condition on perm swap)
  const [activeHandover, setActiveHandover] = useState<Handover | null>(null);
  const [isReturnHandoverOpen, setIsReturnHandoverOpen] = useState(false);
  const [deferredPermSwapPayload, setDeferredPermSwapPayload] =
    useState<RequestPermSwapRequest | null>(null);
  const deferredPermSwapPayloadRef = useRef<RequestPermSwapRequest | null>(
    null
  );

  const maintenanceCategoryLabels: Record<MaintenanceCategory, string> = {
    [MaintenanceCategory.BATTERY_REPLACEMENT]: 'Battery Replacement',
    [MaintenanceCategory.MOTOR_BLDC_REPAIR]: 'Motor BLDC Repair',
    [MaintenanceCategory.TIRE_CHANGE]: 'Tire Change',
    [MaintenanceCategory.BRAKE_SYSTEM]: 'Brake System',
    [MaintenanceCategory.BODY_REPAIR]: 'Body Repair',
    [MaintenanceCategory.ELECTRICAL_SYSTEM]: 'Electrical System',
    [MaintenanceCategory.FRAME_DAMAGE]: 'Frame Damage',
    [MaintenanceCategory.GENERAL_MAINTENANCE]: 'General Maintenance',
    [MaintenanceCategory.OTHER]: 'Other',
  };

  const fetchDetail = useCallback(async () => {
    if (!slug) return;
    setIsLoading(true);
    try {
      const [detail, history] = await Promise.all([
        getMaintenanceById(slug),
        getMaintenanceStatusHistory(slug),
      ]);

      setMaintenance(detail);
      setStatusHistory(history);
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to load maintenance detail')
      );
    } finally {
      setIsLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    fetchDetail();
  }, [fetchDetail, slug]);

  // Fetch active handover for the vehicle (needed for return handover flow)
  useEffect(() => {
    if (!maintenance?.vehicleID) {
      setActiveHandover(null);
      return;
    }

    getListHandover({
      statuses: 'ACTIVE',
      page: '1',
      size: '1000',
    })
      .then((res) => {
        const handover = (res.data || []).find(
          (h) => h.vehicleID === maintenance.vehicleID
        );
        setActiveHandover(handover || null);
      })
      .catch(() => {
        setActiveHandover(null);
      });
  }, [maintenance]);

  const handleMarkComplete = async (payload: {
    workCompleted?: string;
    actualCompletionAt?: string;
    completionImageURL?: string;
    completionNotes: string;
    isCostSameAsEstimate: boolean;
    actualCost: number;
    costVarianceReason?: string;
    tempSwapReturnAt?: string;
    permSwapReturnAt?: string;
  }) => {
    if (!maintenance) return;
    setIsSubmittingComplete(true);
    try {
      await markMaintenanceAsCompleteV2(maintenance.ID, {
        ...payload,
        updatedBy: getUserEmail(),
      });
      showSuccessToast('Success', 'Maintenance marked as complete');
      setIsMarkCompleteOpen(false);
      fetchDetail();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to mark maintenance as complete')
      );
    } finally {
      setIsSubmittingComplete(false);
    }
  };

  const decisionOptions = useMemo(() => {
    const status = maintenance?.status as MaintenanceStatus | undefined;

    if (status === MaintenanceStatus.PENDING_APPROVAL_COMPLETION) {
      return [
        {
          value: 'REJECT',
          title: 'Reject',
          description:
            "Send back to Ops team and negotiate so you don't have to use cost",
          icon: 'ri-error-warning-line',
          accent: 'border-yellow-300',
          badge: 'bg-yellow-100 text-yellow-600',
        },
        {
          value: 'APPROVE',
          title: 'Approve Cost',
          description: 'Accept cost, then close case',
          icon: 'ri-checkbox-circle-line',
          accent: 'border-emerald-300',
          badge: 'bg-emerald-100 text-emerald-600',
        },
      ];
    }

    if (status === MaintenanceStatus.PENDING_APPROVAL_PERM_SWAP) {
      return [
        {
          value: 'REJECT',
          title: 'Reject',
          description: 'Cannot accept permanent swap',
          icon: 'ri-error-warning-line',
          accent: 'border-yellow-300',
          badge: 'bg-yellow-100 text-yellow-600',
        },
        {
          value: 'APPROVE',
          title: 'Approve Permanent Swap',
          description: 'Accept permanent swap',
          icon: 'ri-checkbox-circle-line',
          accent: 'border-emerald-300',
          badge: 'bg-emerald-100 text-emerald-600',
        },
      ];
    }

    return [
      {
        value: 'REJECT',
        title: 'Reject',
        description: 'Cannot accept temporary swap',
        icon: 'ri-error-warning-line',
        accent: 'border-yellow-300',
        badge: 'bg-yellow-100 text-yellow-600',
      },
      {
        value: 'APPROVE',
        title: 'Approve Temporary Swap',
        description: 'Accept temporary swap',
        icon: 'ri-checkbox-circle-line',
        accent: 'border-emerald-300',
        badge: 'bg-emerald-100 text-emerald-600',
      },
    ];
  }, [maintenance?.status]);

  const handleMakeDecision = async (payload: {
    decision: string;
    decisionNotes: string;
  }) => {
    if (!maintenance) return;
    setIsSubmittingDecision(true);
    try {
      await makeMaintenanceDecision(maintenance.ID, {
        fmDecision: payload.decision as 'APPROVE' | 'REJECT',
        fmDecisionNotes: payload.decisionNotes,
        updatedBy: getUserEmail(),
      });
      showSuccessToast('Success', 'Decision submitted');
      setIsDecisionOpen(false);
      fetchDetail();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to submit decision')
      );
    } finally {
      setIsSubmittingDecision(false);
    }
  };

  const handleCloseCase = async () => {
    if (!maintenance) return;
    setIsClosingCase(true);
    try {
      await closeMaintenanceCase(maintenance.ID, {
        updatedBy: getUserEmail(),
      });
      showSuccessToast('Success', 'Maintenance case closed');
      fetchDetail();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to close maintenance case')
      );
    } finally {
      setIsClosingCase(false);
    }
  };

  const handleStartWork = async () => {
    if (!maintenance) return;
    setIsStartingWork(true);
    try {
      await startMaintenanceWork(maintenance.ID, {
        updatedBy: getUserEmail(),
      });
      showSuccessToast('Success', 'Maintenance work started');
      setIsStartWorkOpen(false);
      fetchDetail();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to start maintenance work')
      );
    } finally {
      setIsStartingWork(false);
    }
  };

  const handleSubmitTempSwap = async (payload: {
    tempPlateNumber: string;
    tempModelName: string;
    tempOemName: string;
    tempSwapGivenAt: string;
    tempSwapPhotos: string[];
    tempSwapNotes: string;
  }) => {
    if (!maintenance) return;
    setIsSubmittingTempSwap(true);
    try {
      await requestTempSwap(maintenance.ID, {
        ...payload,
        updatedBy: getUserEmail(),
      });
      showSuccessToast('Success', 'Temporary swap requested');
      setIsTempSwapOpen(false);
      fetchDetail();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to request temporary swap')
      );
    } finally {
      setIsSubmittingTempSwap(false);
    }
  };

  const handleToggleSwapMenu = () => {
    setIsSwapMenuOpen((prev) => !prev);
  };

  const handleRequestTempSwap = () => {
    setIsSwapMenuOpen(false);
    setIsTempSwapOpen(true);
  };

  const handleRequestPermSwap = () => {
    setIsSwapMenuOpen(false);
    setIsPermSwapOpen(true);
  };

  const isPendingApprovalStatus = useMemo(() => {
    const status = maintenance?.status as MaintenanceStatus | undefined;
    return (
      status === MaintenanceStatus.PENDING_APPROVAL_COMPLETION ||
      status === MaintenanceStatus.PENDING_APPROVAL_TEMP_SWAP ||
      status === MaintenanceStatus.PENDING_APPROVAL_PERM_SWAP
    );
  }, [maintenance?.status]);

  const handlePermSwapNext = (selection: 'NEW' | 'EXISTING') => {
    setIsSubmittingPermSwap(true);
    try {
      setIsPermSwapOpen(false);
      if (selection === 'NEW') {
        setIsCreateVehicleOpen(true);
        return;
      }
      setIsSelectReplacementOpen(true);
    } finally {
      setIsSubmittingPermSwap(false);
    }
  };

  const handleSelectReplacement = (vehicle: Vehicle) => {
    setPermSwapNewVehicleId(String(vehicle.ID ?? vehicle.id));
    setPermSwapNewVehicle(null);
    setIsSelectReplacementOpen(false);
    setIsReturnVehicleOpen(true);
  };

  const handleReturnVehicleSuccess = () => {
    setIsReturnVehicleOpen(false);

    // For MINOR_DAMAGE return condition with deferred API call,
    // the payload is captured — open the return handover modal next
    // Use ref for synchronous access (state may not be updated yet)
    if (deferredPermSwapPayloadRef.current && activeHandover) {
      setIsReturnHandoverOpen(true);
    } else {
      setPermSwapNewVehicleId(null);
      setPermSwapNewVehicle(null);
      fetchDetail();
    }
  };

  const handleReturnDataCapture = useCallback(
    (payload: RequestPermSwapRequest) => {
      setDeferredPermSwapPayload(payload);
      deferredPermSwapPayloadRef.current = payload;
    },
    []
  );

  const handleReturnHandoverSubmitOverride = useCallback(
    async (returnHandoverData: ReturnHandoverRequest) => {
      if (!maintenance || !deferredPermSwapPayload) {
        throw new Error('Missing perm swap or maintenance data');
      }

      // Merge return handover data into the perm swap payload
      const payload: RequestPermSwapRequest = {
        ...deferredPermSwapPayload,
        returnHandover: returnHandoverData,
      };

      await requestPermSwap(maintenance.ID, payload);
    },
    [maintenance, deferredPermSwapPayload]
  );

  const handleCreateVehicle = async (vehicleData: any) => {
    try {
      setPermSwapNewVehicleId(null);
      setPermSwapNewVehicle({
        platNomor: vehicleData.platNomor,
        vin: vehicleData.vin,
        imei: vehicleData.imei,
        modelID: vehicleData.modelID,
        color: vehicleData.color,
        contractDuration: vehicleData.contractDuration,
        contractStartDate: vehicleData.contractStartDate,
        arriveAt: vehicleData.arriveAt,
        stnkLink: vehicleData.stnkLink,
        businessUnit: vehicleData.businessUnit,
        hubID: vehicleData.hubID,
        hubName: vehicleData.hubName,
        hubCityID: vehicleData.hubCityID,
        hubCityName: vehicleData.hubCityName,
        vehicleImageURL: vehicleData.vehicleImageURL,
        unitEquipment: vehicleData.unitEquipment || [],
        appUsername: vehicleData.appUsername,
        appPassword: vehicleData.appPassword,
      });
      setIsCreateVehicleOpen(false);
      setIsReturnVehicleOpen(true);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to capture vehicle data'
      );
      showErrorToast('Error', errorMessage);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">
            Loading maintenance details...
          </p>
        </div>
      </div>
    );
  }

  if (!maintenance) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="w-24 h-24 mx-auto mb-4 text-gray-400">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Maintenance Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            The maintenance case with ID "{slug}" could not be found or may have
            been deleted.
          </p>
          <Button
            onClick={() => navigate('/maintenances')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            ← Back to Maintenance List
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto bg-white px-6 py-5">
      <div className="mx-auto">
        <MaintenanceDetailHeader
          maintenance={maintenance}
          onBack={() => navigate('/maintenances')}
          actionButton={getActionButtons(maintenance.status, {
            onMarkComplete: () => setIsMarkCompleteOpen(true),
            onCloseCase: handleCloseCase,
            onStartWork: () => setIsStartWorkOpen(true),
            onMakeDecision: () => setIsDecisionOpen(true),
            onToggleSwapMenu: handleToggleSwapMenu,
            onRequestTempSwap: handleRequestTempSwap,
            onRequestPermSwap: handleRequestPermSwap,
            isSwapMenuOpen: isSwapMenuOpen,
            isMarkingComplete: isSubmittingComplete,
            isClosingCase: isClosingCase,
            isStartingWork: isStartingWork,
            isSubmittingDecision: isSubmittingDecision,
          })}
        />

        <SideBarModal
          isOpen={isStartWorkOpen}
          onClose={() => setIsStartWorkOpen(false)}
          position="center"
          width="lg"
          maxHeight="auto"
          header={
            <div>
              <div className="text-base font-semibold text-gray-900">
                Start Maintenance Work?
              </div>
              <div className="text-xs text-gray-500">
                Confirm the start of maintenance activities to update the
                vehicle status to In Progress.
              </div>
            </div>
          }
          footer={
            <div className="flex w-full justify-end gap-2">
              <Button
                variant="white"
                onClick={() => setIsStartWorkOpen(false)}
                disabled={isStartingWork}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleStartWork}
                disabled={isStartingWork}
              >
                {isStartingWork ? 'Starting...' : 'Start Work'}
              </Button>
            </div>
          }
          className="max-w-[520px]"
        >
          {maintenance && (
            <div className="px-6 py-4">
              <div className="rounded-lg border border-gray-200 p-4 shadow-sm">
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2">
                    <div className="text-gray-500">Plate Number</div>
                    <div className="text-right font-medium text-gray-900">
                      {maintenance.vehiclePlatNomor || '-'}
                    </div>
                  </div>
                  <div className="grid grid-cols-2">
                    <div className="text-gray-500">OEM Name</div>
                    <div className="text-right font-medium text-gray-900">
                      {maintenance.vehicleOemName || '-'}
                    </div>
                  </div>
                  <div className="grid grid-cols-2">
                    <div className="text-gray-500">Models Name</div>
                    <div className="text-right font-medium text-gray-900">
                      {maintenance.vehicleModelName || '-'}
                    </div>
                  </div>
                  <div className="grid grid-cols-2">
                    <div className="text-gray-500">Maintenance Details</div>
                    <div className="text-right font-medium text-gray-900">
                      {maintenance.categories?.length
                        ? maintenance.categories
                            .map(
                              (category) =>
                                maintenanceCategoryLabels[
                                  category as MaintenanceCategory
                                ] || category
                            )
                            .join(', ')
                        : '-'}
                    </div>
                  </div>
                  <div className="grid grid-cols-2">
                    <div className="text-gray-500">OEM Service Center</div>
                    <div className="text-right font-medium text-gray-900">
                      {maintenance.oemServiceCenter || '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </SideBarModal>

        <MakeDecisionModal
          isOpen={isDecisionOpen}
          onClose={() => setIsDecisionOpen(false)}
          onSubmit={handleMakeDecision}
          options={decisionOptions}
          isSubmitting={isSubmittingDecision}
        />

        <RequestTempSwapModal
          isOpen={isTempSwapOpen}
          onClose={() => setIsTempSwapOpen(false)}
          onSubmit={handleSubmitTempSwap}
          isSubmitting={isSubmittingTempSwap}
        />

        <RequestPermSwapModal
          isOpen={isPermSwapOpen}
          onClose={() => setIsPermSwapOpen(false)}
          onNext={handlePermSwapNext}
          isSubmitting={isSubmittingPermSwap}
          maintenance={maintenance}
        />

        <SelectReplacementVehicleModal
          isOpen={isSelectReplacementOpen}
          onClose={() => setIsSelectReplacementOpen(false)}
          onNext={handleSelectReplacement}
        />

        <AddVehicleModal
          isOpen={isCreateVehicleOpen}
          onClose={() => setIsCreateVehicleOpen(false)}
          onSubmit={handleCreateVehicle}
          vehicle={null}
          requirePhotoAndEquipment={true}
        />

        <MaintenanceReturnVehicleModal
          isOpen={isReturnVehicleOpen}
          maintenance={maintenance}
          maintenanceId={maintenance.ID}
          vehicleId={maintenance.vehicleID}
          replacementVehicleId={permSwapNewVehicleId}
          replacementVehicleData={permSwapNewVehicle}
          onClose={() => setIsReturnVehicleOpen(false)}
          onSuccess={handleReturnVehicleSuccess}
          onReturnDataCapture={handleReturnDataCapture}
        />

        <ReturnHandoverModal
          isOpen={isReturnHandoverOpen}
          handover={activeHandover}
          successMessage="Permanent swap and handover return completed successfully"
          errorMessage="Failed to complete permanent swap and handover return"
          onClose={() => {
            setIsReturnHandoverOpen(false);
            setDeferredPermSwapPayload(null);
            deferredPermSwapPayloadRef.current = null;
            setPermSwapNewVehicleId(null);
            setPermSwapNewVehicle(null);
            fetchDetail();
          }}
          onSuccess={() => {
            setIsReturnHandoverOpen(false);
            setDeferredPermSwapPayload(null);
            deferredPermSwapPayloadRef.current = null;
            setPermSwapNewVehicleId(null);
            setPermSwapNewVehicle(null);
            fetchDetail();
          }}
          onSubmitOverride={handleReturnHandoverSubmitOverride}
        />

        <MaintenanceMarkCompleteModal
          isOpen={isMarkCompleteOpen}
          onClose={() => setIsMarkCompleteOpen(false)}
          onSubmit={handleMarkComplete}
          isSubmitting={isSubmittingComplete}
          estimatedCost={maintenance.estimatedCost}
          approvalStatus={maintenance.status}
        />

        {maintenance && isPendingApprovalStatus && (
          <div className="mb-4">
            <StatusNotification maintenance={maintenance} />
          </div>
        )}

        <div className="flex flex-col gap-2 lg:flex-row lg:justify-between">
          <div className="w-full basis-[60%] flex-shrink-0 min-w-0">
            <MaintenanceDetailContent maintenance={maintenance} />
          </div>

          <div className="w-full basis-[40%] flex-shrink-0 min-w-0">
            <MaintenanceStatusTimeline
              statusHistory={statusHistory}
              estimatedCost={maintenance.estimatedCost}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceDetailPage;
